import {
  Description,
  Dashboard as DashboardIcon,
  Group,
  Assignment,
  LocalHospital,
  Assessment,
  LibraryBooks,
  Receipt,
  BarChart,
  BugReport,
  School,
  SupervisorAccount,
  PlaylistAddCheck,
  Poll,
} from '@material-ui/icons';

import { patientDocumentation } from './sections/patientDocumentation';
import { patientListDocumentation } from './sections/patientListDocumentation';
import { overviewDocumentation } from './sections/overviewDocumentation';
import { dashboardDocumentation } from './sections/dashboardDocumentation';
import { claimDocumentation } from './sections/claimDocumentation';
import { claimListDocumentation } from './sections/claimListDocumentation';
import { userDocumentation } from './sections/userDocumentation';
import { userListDocumentation } from './sections/userListDocumentation';
import TestingChecklist from './TestingChecklist';
import ReleaseHistory from './ReleaseHistory';
import { SurveySpecificationPage } from './sections/surveySpecifications';

export const documentationStructure = [
  {
    id: 'overview',
    title: 'Overview',
    icon: <Description />,
    content: {
      title: 'Verdin Labs Project Documentation',
      description: 'Internal documentation for the Verdin Labs development team.',
      tags: ['Overview', 'Getting Started'],
      markdown: overviewDocumentation,
    },
  },
  {
    id: 'survey-specifications',
    title: 'Survey Specifications',
    icon: <Poll />,
    children: [
      {
        id: 'pediatric-health-assessment-new',
        title: 'Pediatric Health Assessment',
        content: {
          title: 'Pediatric Health Assessment Survey',
          description: 'Comprehensive specification for the Pediatric Health Assessment survey form.',
          tags: ['Survey', 'Pediatric', 'Assessment', 'Specification'],
          component: SurveySpecificationPage,
        },
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
    content: {
      title: 'Dashboard Components',
      description: 'Main dashboard functionality and components overview.',
      tags: ['Dashboard', 'Home', 'Navigation'],
      markdown: dashboardDocumentation,
    },
  },
  {
    id: 'accounts',
    title: 'Accounts',
    icon: <Group />,
    children: [
      {
        id: 'account-list',
        title: 'Account List',
        content: {
          title: 'Account List Management',
          description: 'Account listing, filtering, and search functionality.',
          tags: ['Accounts', 'List', 'Management'],
        },
      },
      {
        id: 'account-edit',
        title: 'Account Edit/Create',
        content: {
          title: 'Account Creation and Editing',
          description: 'Forms and workflows for creating and editing account information.',
          tags: ['Accounts', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'patients',
    title: 'Patients',
    icon: <LocalHospital />,
    children: [
      {
        id: 'patient-list',
        title: 'Patient List',
        content: {
          title: 'Patient List Management',
          description: 'Patient listing, search, and filtering functionality.',
          tags: ['Patients', 'List', 'Search'],
          markdown: patientListDocumentation,
        },
      },
      {
        id: 'patient-edit',
        title: 'Patient Edit/Create',
        content: {
          title: 'Patient Creation and Editing',
          description: 'Forms and workflows for managing patient information.',
          tags: ['Patients', 'Edit', 'Create', 'Forms'],
          markdown: patientDocumentation,
        },
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders',
    icon: <Assignment />,
    children: [
      {
        id: 'order-list',
        title: 'Order List',
        content: {
          title: 'Order List Management',
          description: 'Order listing, tracking, and management functionality.',
          tags: ['Orders', 'List', 'Management'],
        },
      },
      {
        id: 'order-edit',
        title: 'Order Edit/Create',
        content: {
          title: 'Order Creation and Editing',
          description: 'Forms and workflows for creating and editing orders.',
          tags: ['Orders', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'assessments',
    title: 'Assessments',
    icon: <Assessment />,
    children: [
      {
        id: 'assessment-list',
        title: 'Assessment List',
        content: {
          title: 'Assessment List Management',
          description: 'Assessment listing, filtering, and review functionality.',
          tags: ['Assessments', 'List', 'Management'],
        },
      },
      {
        id: 'assessment-edit',
        title: 'Assessment Edit/Create',
        content: {
          title: 'Assessment Creation and Editing',
          description: 'Forms and workflows for creating and editing assessments.',
          tags: ['Assessments', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    icon: <LibraryBooks />,
    children: [
      {
        id: 'resource-list',
        title: 'Resource List',
        content: {
          title: 'Resource List Management',
          description: 'Resource listing, categorization, and management functionality.',
          tags: ['Resources', 'List', 'Management'],
        },
      },
      {
        id: 'resource-edit',
        title: 'Resource Edit/Create',
        content: {
          title: 'Resource Creation and Editing',
          description: 'Forms and workflows for creating and editing resources.',
          tags: ['Resources', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'claims',
    title: 'Claims Reporting',
    icon: <Receipt />,
    children: [
      {
        id: 'claim-list',
        title: 'Claims List',
        content: {
          title: 'Claims Reporting List',
          description: 'Claims listing, filtering, and reporting functionality.',
          tags: ['Claims', 'List', 'Reporting'],
          markdown: claimListDocumentation,
        },
      },
      {
        id: 'claim-edit',
        title: 'Claims Edit/Create',
        content: {
          title: 'Claims Creation and Editing',
          description: 'Forms and workflows for creating and editing claims.',
          tags: ['Claims', 'Edit', 'Create', 'Forms'],
          markdown: claimDocumentation,
        },
      },
    ],
  },
  {
    id: 'claim-reports',
    title: 'Claim Reports',
    icon: <BarChart />,
    children: [
      {
        id: 'claim-report-list',
        title: 'Claim Report List',
        content: {
          title: 'Claim Report List Management',
          description: 'Claim report listing, generation, and management functionality.',
          tags: ['Claim Reports', 'List', 'Management'],
        },
      },
      {
        id: 'claim-report-edit',
        title: 'Claim Report Edit/Create',
        content: {
          title: 'Claim Report Creation and Editing',
          description: 'Forms and workflows for creating and editing claim reports.',
          tags: ['Claim Reports', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'reportables',
    title: 'Reportables',
    icon: <BugReport />,
    children: [
      {
        id: 'reportable-list',
        title: 'Reportable List',
        content: {
          title: 'Reportable List Management',
          description: 'Reportable event listing, tracking, and management functionality.',
          tags: ['Reportables', 'List', 'Management'],
        },
      },
      {
        id: 'reportable-edit',
        title: 'Reportable Edit/Create',
        content: {
          title: 'Reportable Creation and Editing',
          description: 'Forms and workflows for creating and editing reportable events.',
          tags: ['Reportables', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'programs',
    title: 'Programs',
    icon: <School />,
    children: [
      {
        id: 'program-list',
        title: 'Program List',
        content: {
          title: 'Program List Management',
          description: 'Program listing, categorization, and management functionality.',
          tags: ['Programs', 'List', 'Management'],
        },
      },
      {
        id: 'program-edit',
        title: 'Program Edit/Create',
        content: {
          title: 'Program Creation and Editing',
          description: 'Forms and workflows for creating and editing programs.',
          tags: ['Programs', 'Edit', 'Create', 'Forms'],
        },
      },
    ],
  },
  {
    id: 'allergi-end-resources',
    title: 'AllergiEnd Documents',
    icon: <Description />,
    content: {
      title: 'AllergiEnd Documents',
      description: 'AllergiEnd related documentation and resources management.',
      tags: ['AllergiEnd', 'Documents', 'Resources'],
    },
  },
  {
    id: 'users',
    title: 'Users',
    icon: <SupervisorAccount />,
    children: [
      {
        id: 'user-list',
        title: 'User List',
        content: {
          title: 'User List Management',
          description: 'User listing, search, and filtering functionality.',
          tags: ['Users', 'List', 'Management'],
          markdown: userListDocumentation,
        },
      },
      {
        id: 'user-edit',
        title: 'User Edit/Create',
        content: {
          title: 'User Creation and Editing',
          description: 'Forms and workflows for creating and editing users.',
          tags: ['Users', 'Edit', 'Create', 'Forms'],
          markdown: userDocumentation,
        },
      },
    ],
  },
  {
    id: 'release-history',
    title: 'Release History',
    icon: <Description />,
    content: {
      title: 'Release Change History',
      description: 'Track documentation changes across versions with detailed overview.',
      tags: ['History', 'Releases', 'Changes', 'Versions'],
      component: ReleaseHistory,
    },
  },
  {
    id: 'testing-scenarios',
    title: 'Testing Scenarios',
    icon: <PlaylistAddCheck />,
    content: {
      title: 'Interactive Testing Checklist',
      description: 'Comprehensive testing scenarios with interactive checkboxes to track progress.',
      tags: ['Testing', 'QA', 'Checklist', 'Scenarios'],
      component: TestingChecklist,
    },
  },
];
